import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";

function mondayOf(d = new Date()) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date.toISOString().slice(0, 10);
}

export default function Payroll() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", week_start: mondayOf(), tax_rate: 20 });

  const load = () => api.get("/payroll").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setForm((f) => ({ ...f, company_id: user?.role === "manager" ? user.company_id : r.data[0]?.id || "" }));
    });
    load();
  }, [user]);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.company_id) return toast.error("Select a company");
    try {
      const { data } = await api.post("/payroll/generate", {
        company_id: form.company_id, week_start: form.week_start, tax_rate: Number(form.tax_rate) / 100,
      });
      if (data.totals.employees === 0)
        toast.warning("Draft created, but no tracked hours fell in this week. Add timesheet entries, then regenerate.");
      else
        toast.success(`Payroll drafted · ${money(data.totals.net)} net`);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Wages</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Payroll</h1>
          <p className="text-slate-500 mt-1">Generate weekly runs, then submit for back-end verification.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="generate-payroll-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><Plus className="w-4 h-4 mr-1" /> New payroll run</Button>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Company</th>
              <th className="text-left font-semibold px-6 py-3">Week of</th>
              <th className="text-left font-semibold px-6 py-3">Employees</th>
              <th className="text-left font-semibold px-6 py-3">Gross</th>
              <th className="text-left font-semibold px-6 py-3">Net</th>
              <th className="text-left font-semibold px-6 py-3">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {runs.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400">No payroll runs yet. Generate your first one.</td></tr>}
            {runs.map((p) => (
              <tr key={p.id} data-testid={`payroll-row-${p.id}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 font-semibold text-slate-800">{p.company_name}</td>
                <td className="px-6 py-4 text-slate-600">{new Date(p.week_start).toLocaleDateString()}</td>
                <td className="px-6 py-4 font-mono text-slate-700">{p.totals.employees}</td>
                <td className="px-6 py-4 font-mono text-slate-700">{money(p.totals.gross)}</td>
                <td className="px-6 py-4 font-mono font-bold text-sky-600">{money(p.totals.net)}</td>
                <td className="px-6 py-4"><StatusBadge status={p.status} /></td>
                <td className="px-6 py-4 text-right">
                  <Link to={`/app/payroll/${p.id}`} data-testid={`open-payroll-${p.id}`} className="inline-flex items-center gap-1 text-sky-600 font-semibold text-sm hover:gap-2 transition-all">Open <ArrowRight className="w-4 h-4" /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate payroll run</DialogTitle></DialogHeader>
          <form onSubmit={generate} className="space-y-4">
            <div>
              <Label>Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })} disabled={user?.role === "manager"}>
                <SelectTrigger className="mt-1.5" data-testid="payroll-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Week starting (Mon)</Label><Input data-testid="payroll-week-input" type="date" value={form.week_start} onChange={(e) => setForm({ ...form, week_start: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Tax estimate (%)</Label><Input data-testid="payroll-tax-input" type="number" step="1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="mt-1.5 font-mono" /></div>
            </div>
            <p className="text-xs text-slate-500">Wages are computed from completed timesheet entries within the selected week.</p>
            <DialogFooter><Button type="submit" data-testid="confirm-generate-payroll" className="bg-sky-600 hover:bg-sky-700">Generate</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
